import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import { PiSignOut } from "react-icons/pi";
import { getFreshAccessToken } from "../chat/api";
import DeleteAccountPopup from "../chat/DeleteAccountPopup";
import api from "../chat/api";
import {
  PageHeader,
  SettingRow,
  TextInput,
  LockedField,
  SecondaryButton,
  DangerBox,
  SaveBar,
  useSettings,
} from "./settings/ui";

const ROLE_INFO = {
  Admin: "Full control of the workspace, including members and billing.",
  Editor:
    "Works on projects and proposals, and can create personal section templates. Ask an admin if you need a different role.",
  Reviewer:
    "Views projects and proposals and leaves comments. Ask an admin if you need a different role.",
};

const AccountInfo = () => {
  const { orgRole, workspaceName } = useSettings();
  const [fullName, setFullName] = useState("");
  const [draftName, setDraftName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const storedFullName =
      localStorage.getItem("fullName") ||
      localStorage.getItem("full_name") ||
      localStorage.getItem("username") ||
      "John Doe";
    const storedEmail = localStorage.getItem("email") || "example@gmail.com";
    setFullName(storedFullName);
    setDraftName(storedFullName);
    setEmail(storedEmail);
  }, []);

  const roleName = { Owner: "Admin", Member: "Editor" }[orgRole] || orgRole;
  const roleLine = roleName ? `${roleName} in ${workspaceName}` : "";
  const dirty = draftName !== fullName;

  const handleSave = async () => {
    const name = draftName.trim();
    if (!name) {
      alert("Full Name cannot be empty.");
      return;
    }
    setSaving(true);
    await getFreshAccessToken();
    try {
      const response = await api.patch("api/v1/accounts/update-full-name/", {
        full_name: name,
      });
      if (response.status === 200) {
        setFullName(response.data.full_name);
        setDraftName(response.data.full_name);
        localStorage.setItem("fullName", response.data.full_name);
        localStorage.setItem("full_name", response.data.full_name);
      } else {
        alert("Failed to update name.");
      }
    } catch (error) {
      console.error("Error updating name:", error);
      alert("An error occurred while updating the name. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/signin";
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    const token = Cookies.get("access_token");

    if (!token) {
      alert("User is not authenticated. Please log in.");
      setIsDeleting(false);
      setShowDeletePopup(false);
      return;
    }

    try {
      const response = await api.delete(`api/v1/accounts/delete-account/`);
      if (response.status === 200) {
        localStorage.clear();
        window.location.href = "/signin";
      } else {
        alert("Failed to delete account. Please try again.");
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      alert("An error occurred while trying to delete your account.");
    } finally {
      setIsDeleting(false);
      setShowDeletePopup(false);
    }
  };

  return (
    <div className="w-full font-outfit">
      <PageHeader
        scope="You"
        title="Profile"
        description="How you appear to teammates. Only you can change these."
      />

      <div className="max-w-[760px]">
        <div className="flex items-center gap-4 pb-6">
          <span className="w-14 h-14 shrink-0 rounded-full bg-[#C95EBE] text-white text-[22px] font-bold flex items-center justify-center">
            {fullName ? fullName.charAt(0).toUpperCase() : "U"}
          </span>
          <div className="min-w-0">
            <div className="text-[16px] font-semibold text-[#18101C] truncate">{fullName}</div>
            <div className="text-[14px] text-[#6B7280] truncate">
              {email}
              {roleLine && workspaceName ? ` · ${roleLine}` : ""}
            </div>
          </div>
        </div>

        <SettingRow label="Full name" help="Shown on proposals, comments and team lists.">
          <TextInput
            value={draftName}
            readOnly={saving}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && dirty && handleSave()}
          />
        </SettingRow>

        <SettingRow
          label="Email address"
          help="Your sign-in codes are sent here. Contact support to change it."
        >
          <LockedField>{email}</LockedField>
        </SettingRow>

        {roleName && (
          <SettingRow label="Your role" help={workspaceName ? `In ${workspaceName}.` : undefined}>
            <div className="max-w-[420px] flex flex-col gap-1.5">
              <span className="self-start text-[13px] font-semibold text-[#7C3AED] bg-[rgba(139,85,221,0.1)] px-2.5 py-[3px] rounded-full">
                {roleName}
              </span>
              {ROLE_INFO[roleName] && (
                <span className="text-[13px] leading-[1.45] text-[#71717A]">{ROLE_INFO[roleName]}</span>
              )}
            </div>
          </SettingRow>
        )}

        <SettingRow label="Sessions" help="Sign out of DocuSensa on this device.">
          <SecondaryButton icon={PiSignOut} onClick={handleLogout}>
            Log out
          </SecondaryButton>
        </SettingRow>

        <DangerBox
          title="Delete account"
          description="Permanently removes your account and all its data. This can't be undone."
          actionLabel="Delete account…"
          onAction={() => setShowDeletePopup(true)}
        />
      </div>

      <SaveBar
        show={dirty}
        saving={saving}
        onDiscard={() => setDraftName(fullName)}
        onSave={handleSave}
      />

      <DeleteAccountPopup
        isOpen={showDeletePopup}
        onClose={() => setShowDeletePopup(false)}
        onDelete={handleDeleteAccount}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default AccountInfo;
